import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaPlay, FaFire } from "react-icons/fa";
import { useTitle } from "../hooks/useTitle";
import { useUser } from "../context/UserContext";

function Game() {
  useTitle("Game Hub");
  const navigate = useNavigate();
  const { userData } = useUser();
  const unclaimedBetsCount = userData?.user?.unclaimedBetsCount || 0;

  const games = [
    {
      id: "football",
      title: "Football Prediction",
      description: "Bet gems on real football matches around the world! Win big multipliers.",
      image: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=500&q=80",
      route: "/game/football",
      color: "from-green-500 to-emerald-700",
      shadow: "shadow-emerald-500/40",
      tag: "Sports"
    },
    {
      id: "spin",
      title: "Lucky Spin",
      description: "Spin the premium wheel for a chance to win massive Gems and Tickets!",
      image: "/images/lucky_spin.png",
      route: "/game/spin",
      color: "from-indigo-500 to-purple-600",
      shadow: "shadow-purple-500/40",
      tag: "Popular"
    },
    {
      id: "coinflip",
      title: "Coin Flip",
      description: "Double your crypto gems with a 50/50 flip. Risk it all for huge rewards!",
      image: "/images/coin_flip.png",
      route: "/game/coinflip",
      color: "from-amber-400 to-orange-600",
      shadow: "shadow-orange-500/40",
      tag: "Hot"
    },
    {
      id: "chest",
      title: "Mystery Chest",
      description: "Open a mystery chest for 50 💎 and uncover hidden gems. A 500 💎 jackpot awaits the lucky one!",
      image: "/images/mystery_chest.png",
      route: "/game/chest",
      color: "from-emerald-500 to-teal-600",
      shadow: "shadow-emerald-500/40",
      tag: "Jackpot"
    },
    {
      id: "scratch",
      title: "Scratch & Win",
      description: "Buy a scratch card and reveal your prize! Shares the massive global 500 💎 Jackpot.",
      image: "https://images.unsplash.com/photo-1518133835878-5a93cc3f89e5?w=500&q=80",
      route: "/game/scratch",
      color: "from-cyan-500 to-blue-600",
      shadow: "shadow-cyan-500/40",
      tag: "New"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-slate-50 dark:bg-[#0a0f1a] text-slate-900 dark:text-white min-h-screen pb-28 pt-20 transition-colors duration-300"
    >
      <div className="container mx-auto px-4 mt-2">
        <div className="flex flex-col items-center justify-center mb-10 text-center">
          <motion.div 
            initial={{ scale: 0 }} 
            animate={{ scale: 1 }} 
            className="w-16 h-16 bg-gradient-to-tr from-fuchsia-500 to-rose-500 rounded-3xl flex items-center justify-center mb-4 shadow-lg shadow-rose-500/30 rotate-3"
          >
            <FaFire size={28} className="text-white drop-shadow-md" />
          </motion.div>
          <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-slate-800 to-slate-500 dark:from-white dark:to-slate-400 tracking-tight" style={{ lineHeight: '1.2' }}>
            Game Hub
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mt-2 max-w-xs">Play premium mini-games to boost your gem balance rapidly!</p>
        </div>
        
        <div className="flex flex-col gap-8 max-w-md mx-auto">
          {games.map((game, i) => (
            <motion.div
              key={game.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.15, type: "spring", stiffness: 200, damping: 20 }}
              whileHover={{ scale: 1.03, y: -5 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(game.route)}
              className={`relative cursor-pointer rounded-[2.5rem] overflow-hidden shadow-2xl ${game.shadow} bg-slate-900 border border-slate-800 group`}
            >
              {/* Image Section */}
              <div className="w-full h-56 relative overflow-hidden bg-black flex items-center justify-center">
                <img 
                  src={game.image} 
                  alt={game.title} 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent mix-blend-multiply"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
                
                {/* Tag */}
                <div className="absolute top-5 left-5 bg-black/40 backdrop-blur-md border border-white/10 px-4 py-1.5 rounded-full z-10">
                  <span className="text-xs font-bold text-white uppercase tracking-widest">{game.tag}</span>
                </div>
                {game.id === "football" && unclaimedBetsCount > 0 && (
                  <div className="absolute top-5 right-5 bg-red-500 border border-red-300/30 px-3 py-1.5 rounded-full z-10 flex items-center justify-center animate-pulse shadow-lg shadow-red-500/40">
                    <span className="text-[10px] font-bold text-white uppercase tracking-widest">{unclaimedBetsCount} WIN{unclaimedBetsCount > 1 ? 'S' : ''}</span>
                  </div>
                )}
              </div>

              {/* Text Section */}
              <div className="relative p-6 pt-2 bg-slate-900">
                <h3 className="text-2xl font-black text-white mb-2">{game.title}</h3>
                <p className="text-sm text-slate-300 font-medium mb-6 leading-relaxed">
                  {game.description}
                </p>
                
                <button className={`w-full py-4 rounded-2xl font-bold text-white transition-all bg-gradient-to-r ${game.color} shadow-lg flex items-center justify-center gap-2 group-hover:shadow-xl ${game.shadow}`}>
                  <FaPlay size={14} className="opacity-90" /> Play Now
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default Game;
